import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, HelpOutlineIcon, Link, TextField, Typography } from '@esgian/esgianui';
import { useForm, Controller } from 'react-hook-form';
import ActionModal from '../../../../Common/ActionModal';
import TextWithTooltipIcon from '../../../../Display/TextWithTooltipIcon/TextWithTooltipIcon';
import EditableMainEngineFuelTable from './EditableMainEngineFuelTable';
const MAIN_ENGINE_FUEL_TOOLTIP =
  'Main engine fuel corresponds to the primary fuel type on the vessel used for propelling the vessel. Esgian has assumed a fuel type compatible with the engine for all voyage legs. Edit the fuel type  of the voyage leg which you want to modify. All the other voyage legs which are not specified will continue using the assumed default fuel type.';
const AUX_ENGINE_FUEL_TOOLTIP =
  'Main generator fuel corresponds to the fuel type used for generating auxiliary power for the vessel. Esgian has assumed a fuel type compatible with the engine for all voyage leg. The fuel type is customized automatically as per customization in main engine fuel type. The user may overwrite it if needed.';

const getInitialTableData = (defaultFuelConfiguration, voyagePortCalls, allFuelTypes) => {
  if (!defaultFuelConfiguration || !voyagePortCalls || !voyagePortCalls.length) {
    return [];
  }

  const initialData = defaultFuelConfiguration.map(({ fromPortId, toPortId, fuelTypeId }) => {
    const fromPort = voyagePortCalls.find((port) => port.portId === fromPortId);
    const toPort = voyagePortCalls.find((port) => port.portId === toPortId);

    return {
      fuelType: allFuelTypes.find((f) => f.id === fuelTypeId),
      fromPort,
      toPort
    };
  });

  return initialData;
};

const EditEstimatedEtsDataModal = ({
  open,
  onClose,
  onSave,
  voyagePortCalls = [],
  fuelDetails,
  customCalculationData
}) => {
  const [showInfo, setShowInfo] = useState(true);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset
  } = useForm({
    mode: 'onChange'
  });

  const [mainEngineFuelTableData, setMainEngineFuelTableData] = useState([]);
  const [auxEngineFuelTableData, setAuxEngineFuelTableData] = useState([]);
  if (!fuelDetails) return null;

  const { mainEngineSupportedFuelTypeIds, auxEngineSupportedFuelTypeIds, fuelTypeIdsFuelNames } =
    fuelDetails;

  const { mainEngineFuelChange, auxEngineFuelChange } = customCalculationData;

  const allFuelTypes = Object.keys(fuelTypeIdsFuelNames ?? {}).map((key) => ({
    id: parseInt(key),
    name: fuelTypeIdsFuelNames[key]
  }));

  const mainEngineSupportedFuels = allFuelTypes.filter((fuel) =>
    mainEngineSupportedFuelTypeIds.includes(fuel.id)
  );

  const auxEngineSupportedFuels = allFuelTypes.filter((fuel) =>
    auxEngineSupportedFuelTypeIds.includes(fuel.id)
  );

  useEffect(() => {
    setMainEngineFuelTableData(
      getInitialTableData(
        customCalculationData?.mainEngineFuelChange,
        voyagePortCalls,
        allFuelTypes
      )
    );
    setAuxEngineFuelTableData(
      getInitialTableData(customCalculationData?.auxEngineFuelChange, voyagePortCalls, allFuelTypes)
    );
  }, [customCalculationData, voyagePortCalls]);

  const onMainEngineFuelTableDataChange = (data) => {
    setMainEngineFuelTableData(data);
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  const content = (
    <Box px={2} py={4} display="flex" flexDirection="column" gap={2}>
      {showInfo && (
        <Alert
          onClose={() => setShowInfo(false)}
          icon={<HelpOutlineIcon sx={{ color: 'black' }} />}
          severity="info"
          sx={{ backgroundColor: 'text.secondary', color: 'black' }}>
          Modify your cargo weight, EU Allowance cost and fuel type of a voyage leg to calculate
          customized results. You can toggle between customized values and Esgian default values
          using the drop down near the ‘Modify button’ after recalculation.
        </Alert>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <TextWithTooltipIcon
          label="ETS cost per ton CO2(€) "
          tooltipText={
            <Typography>
              Cost per EU allowance (EUAs). Referred from{' '}
              <Link
                style={{ display: 'inline' }}
                href="https://www.ice.com/products/197/EUA-Futures/data?marketId=7440405&span=1">
                here
              </Link>
            </Typography>
          }
        />
        <Controller
          name="etsCostValue"
          defaultValue={customCalculationData?.etsCostValue}
          control={control}
          rules={{
            min: { value: 0, message: 'Value must be ≥ 0' },
            pattern: { value: /^\d*\.?\d*$/, message: 'Invalid number' }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              variant="standard"
              InputProps={{ inputProps: { min: 0 } }}
              error={!!errors.etsCostValue}
              helperText={errors.etsCostValue?.message}
              sx={{ width: '100px' }}
            />
          )}
        />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <TextWithTooltipIcon
          label="My cargo weight (t)"
          tooltipText={
            <Typography>
              The weight of the cargo (in tonnes) that you want to calculate the ETS for.
            </Typography>
          }
        />
        <Controller
          name="myCargoWeight"
          defaultValue={customCalculationData?.myCargoWeight}
          control={control}
          rules={{
            min: { value: 0, message: 'Value must be ≥ 0' },
            pattern: { value: /^\d*\.?\d*$/, message: 'Invalid number' }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              variant="standard"
              InputProps={{ inputProps: { min: 0 } }}
              error={!!errors.myCargoWeight}
              helperText={errors.myCargoWeight?.message}
              sx={{ width: '100px' }}
            />
          )}
        />
      </Box>
      <Controller
        name="mainEngineFuelChange"
        control={control}
        render={({ field: { onChange }, fieldState: { error } }) => (
          <EditableMainEngineFuelTable
            voyagePortCalls={voyagePortCalls}
            onTableDataChange={onChange}
            errorMessage={error?.message}
            title="Main engine Fuel"
            titleTooltip={MAIN_ENGINE_FUEL_TOOLTIP}
            supportedFuels={mainEngineSupportedFuels}
            allFuelTypes={allFuelTypes}
            defaultFuelConfiguration={mainEngineFuelChange}
            tableData={mainEngineFuelTableData}
            setTableData={onMainEngineFuelTableDataChange}
          />
        )}
        // rules={{
        //   validate: {
        //     hasValidData: (value) =>
        //       (value && value.length > 0) || 'At least one fuel record is required.'
        //   }
        // }}
      />
      <Controller
        name="auxEngineFuelChange"
        control={control}
        render={({ field: { onChange }, fieldState: { error } }) => (
          <EditableMainEngineFuelTable
            voyagePortCalls={voyagePortCalls}
            onTableDataChange={onChange}
            errorMessage={error?.message}
            title="Main generator Fuel"
            titleTooltip={AUX_ENGINE_FUEL_TOOLTIP}
            supportedFuels={auxEngineSupportedFuels}
            allFuelTypes={allFuelTypes}
            defaultFuelConfiguration={auxEngineFuelChange}
            tableData={auxEngineFuelTableData}
            setTableData={setAuxEngineFuelTableData}
          />
        )}
        // rules={{
        //   validate: {
        //     hasValidData: (value) =>
        //       (value && value.length > 0) || 'At least one fuel record is required.'
        //   }
        // }}
      />
    </Box>
  );

  return (
    <ActionModal
      title="Modify Data for Recalculation"
      open={open}
      content={content}
      onAgree={handleSubmit(onSubmit)}
      onCancel={() => {
        reset({});
        onClose();
      }}
      agreeButtonLabel="look good, submit"
      cancelButtonLabel="will do it later"
      maxWidth="md"
      isDisabledAgree={!isValid}
    />
  );
};

EditEstimatedEtsDataModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  voyagePortCalls: PropTypes.array,
  fuelDetails: PropTypes.object,
  customCalculationData: PropTypes.object
};

export default EditEstimatedEtsDataModal;
